import log from 'loglevel';
import * as types from "./ActionTypes";
import { getAuthHeaders, syncTokenToStorage, generateFingerprint } from "analytics/utils/utils";
import httpService from 'services/httpService';
// ====== BASIC ACTION CREATORS ======

export const setCampaignData = (campaignData) => ({
  type: types.SET_CAMPAIGN_DATA,
  payload: campaignData,
});

export const setPageStartTime = () => ({
  type: types.SET_PAGE_START_TIME,
  payload: Date.now(),
});

export const setFingerprint = (fingerprint) => ({
  type: types.SET_FINGERPRINT,
  payload: fingerprint,
});

export const setSessionData = (sessionData) => ({
  type: types.SET_SESSION_DATA,
  payload: sessionData,
});

export const clearError = () => ({
  type: types.CLEAR_ERROR,
});

export const setCurrentHitId = (hitId) => ({
  type: types.SET_CURRENT_HIT_ID,
  payload: hitId,
});

// ====== TOKEN MANAGEMENT ======

export const syncToken = (token) => {
  syncTokenToStorage(token);
  return {
    type: types.SYNC_TOKEN,
    payload: token,
  };
};

export const handleTokenChange = (newToken, oldToken) => {
  return async (dispatch) => {
    // Sync token to storage
    dispatch(syncToken(newToken));
    
    // Track authentication state change
    if (newToken && !oldToken) {
      // User logged in
      log.log('Analytics: User logged in');
    } else if (!newToken && oldToken) {
      // User logged out
      log.log('Analytics: User logged out');
    } else if (newToken !== oldToken) {
      // Token refreshed
      log.log('Analytics: Token refreshed');
    }
  };
};

// ====== ENHANCED TRACKING ACTIONS ======

// Initialize analytics system
export const initializeAnalytics = (initialData = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.ANALYTICS_INIT_REQUEST });

      // Generate fingerprint if not provided
      const fingerprint = initialData.fingerprint || await generateFingerprint();
      dispatch(setFingerprint(fingerprint));

      // Get current token
      const state = getState();
      const token = state.userInfo?.token;
      if (token) {
        dispatch(syncToken(token));
      }

      dispatch({ type: types.ANALYTICS_INIT_SUCCESS });
      log.log('Analytics: Initialized successfully');
    } catch (error) {
      dispatch({
        type: types.ANALYTICS_INIT_FAILURE,
        payload: error.message,
      });
      log.error('Analytics: Initialization failed', error);
    }
  };
};

// Main tracking function
export const trackPageHit = (pageData = {}) => {
  return async (dispatch, getState) => {
    dispatch({ type: types.TRACK_PAGE_REQUEST });

    try {
      const state = getState();
      const analytics = state.analytics || {};
      
      // Get token from userInfo
      const token = state.userInfo?.token;
      
      // Prepare tracking data
      const trackingData = {
        session_id: analytics.sessionId || null,
        fingerprint: analytics.fingerprint,
        campaign_id: analytics.campaignId || null,
        ...analytics.utmData,
        page_url: pageData.url || window.location.href,
        page_title: pageData.title || document.title,
        referrer: document.referrer,
        event_type: pageData.event_type || 'page_view',
        ...pageData,
      };

      // Remove undefined values
      Object.keys(trackingData).forEach(key => {
        if (trackingData[key] === undefined) {
          delete trackingData[key];
        }
      });

      const headers = getAuthHeaders(token);
      const response = await httpService.post('/analytics/track/', trackingData, { headers });

      // Update analytics state with response
      if (response.data.session_id) {
        dispatch(setSessionData({
          sessionId: response.data.session_id,
          lastHitId: response.data.hit_id,
        }));
      }

      if (response.data.hit_id) {
        dispatch(setCurrentHitId(response.data.hit_id));
      }

      dispatch({
        type: types.TRACK_PAGE_SUCCESS,
        payload: response.data,
      });

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({
        type: types.TRACK_PAGE_FAILURE,
        payload: message,
      });
      throw error;
    }
  };
};

// Update time on page
export const updateTimeOnPage = (hitId, timeOnPage) => {
  return async (dispatch, getState) => {
    if (!hitId || timeOnPage === null || timeOnPage === undefined) {
      log.warn('Analytics: Invalid parameters for updateTimeOnPage');
      return;
    }

    dispatch({ type: types.UPDATE_TIME_REQUEST });

    try {
      const state = getState();
      const token = state.userInfo?.token;
      const headers = getAuthHeaders(token);
      
      const response = await httpService.post('/analytics/update-time/', {
        hit_id: hitId,
        time_on_page: timeOnPage,
      }, { headers });

      dispatch({
        type: types.UPDATE_TIME_SUCCESS,
        payload: response.data,
      });

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({
        type: types.UPDATE_TIME_FAILURE,
        payload: message,
      });
      // Don't throw for time updates as they're not critical
      log.warn('Analytics: Failed to update time on page', message);
    }
  };
};

// ====== SPECIALIZED TRACKING ACTIONS ======

// Track custom events
export const trackCustomEvent = (eventName, eventData = {}) => {
  return (dispatch) => {
    const customPageData = {
      event_type: 'custom_event',
      custom_event_name: eventName,
      event_data: typeof eventData === 'object' ? eventData : { value: eventData },
      custom_event_timestamp: new Date().toISOString(),
    };
    
    return dispatch(trackPageHit(customPageData));
  };
};

// Track user actions
export const trackUserAction = (action, additionalData = {}) => {
  return (dispatch, getState) => {
    const state = getState();
    const actionData = {
      event_type: 'user_action',
      action: action,
      user_mode: state.userInfo?.mode,
      is_authenticated: !!state.userInfo?.token,
      action_timestamp: new Date().toISOString(),
      ...additionalData
    };
    
    return dispatch(trackPageHit(actionData));
  };
};

// Track errors
export const trackError = (error, context = {}) => {
  return (dispatch) => {
    const errorData = {
      event_type: 'error',
      error_message: error.message || 'Unknown error',
      error_stack: error.stack || '',
      error_name: error.name || 'Error',
      error_timestamp: new Date().toISOString(),
      ...context,
    };

    return dispatch(trackPageHit(errorData));
  };
};

// Track performance metrics
export const trackPerformance = (performanceData = {}) => {
  return (dispatch) => {
    const perfData = {
      event_type: 'performance',
      performance_timestamp: new Date().toISOString(),
      ...performanceData,
    };

    return dispatch(trackPageHit(perfData));
  };
};

// Track route changes
export const trackRouteChange = (fromPath, toPath, additionalData = {}) => {
  return (dispatch) => {
    const routeData = {
      event_type: 'route_change',
      from_path: fromPath,
      to_path: toPath,
      route_change_timestamp: new Date().toISOString(),
      ...additionalData,
    };

    return dispatch(trackPageHit(routeData));
  };
};

// Update campaign data from URL
export const updateCampaignFromUrl = () => {
  return (dispatch) => {
    const urlParams = new URLSearchParams(window.location.search);
    const campaignData = {
      campaign_id: urlParams.get('campaign_id') || urlParams.get('cid'),
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
    };

    // Only dispatch if there's campaign data
    const hasData = Object.values(campaignData).some(v => v);
    if (hasData) {
      dispatch(setCampaignData(campaignData));
    }
  };
};

// Debounced version of updateTimeOnPage
let updateTimeDebounceTimer = null;
export const updateTimeOnPageDebounced = (hitId, timeOnPage) => {
  return (dispatch) => {
    if (updateTimeDebounceTimer) {
      clearTimeout(updateTimeDebounceTimer);
    }

    updateTimeDebounceTimer = setTimeout(() => {
      dispatch(updateTimeOnPage(hitId, timeOnPage));
    }, 1000);
  };
};

// Cleanup analytics state
export const cleanupAnalytics = () => {
  return (dispatch) => {
    dispatch({ type: types.CLEAR_SESSION });
    dispatch({ type: types.CLEAR_CAMPAIGN_DATA });
    dispatch({ type: types.CLEAR_PAGE_START_TIME });
    log.log('Analytics: Cleanup completed');
  };
};